/**
 * One Clear Thing — the redesigned /today page
 * ============================================
 *
 * Design thesis: the anxious user opens /today already composing a guilty
 * inventory in their head. The page's first job is physiological, not
 * informational — lower the heart rate before asking for anything. The fold
 * holds four calm objects: the date, yanamas' message, a wordless capacity
 * line, and ONE veiled next step. Everything else exists one tap away.
 *
 * Doctrine (structural, not decorative):
 *  - Capacity framing, never worth framing: no percentage bar, no stat row,
 *    no scoreboard. Units appear only inside two sanctioned disclosures
 *    (explainUnits — see energyModeCopy.ts).
 *  - Celebration + permission to stop FIRST when the core is done
 *    (permissionToStopCard verbatim); optional work strictly second.
 *  - Reset framing for carryover; "set down", never "skipped" in warn tones.
 *  - R9: mood is render-only — no code path from mood to limits or copy.
 *  - Privacy: no task titles visible without an intentional tap (veil +
 *    collapsed sections), EXCEPT when safety requires it (deadline days
 *    auto-reveal — calm never trumps safety).
 *
 * Same props contract as TodayFocusRefined; the data layer is untouched.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { FocusSetup, saveFocusPlan } from '../components/reminders/FocusSetup'
import { ObligationStrip } from '../components/reminders/ObligationStrip'
import { TaskQuickAdd } from '../components/tasks/TaskQuickAdd'
import { DeadlineCarousel } from '../components/today/DeadlineCarousel'
import { ExecutionTimeline } from '../components/today/ExecutionTimeline'
import { PlanAdjustments } from '../components/today/PlanAdjustments'
import type { TimelineRow } from '../components/today/timelineModel'
import { fetchDayShape } from '../services/dayShapeService'
import { fetchFreeSlots } from '../services/freeSlotsService'
import { todayFreeMinutes } from '../utils/freeTime'
import {
  AlertCircle,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  Clock3,
  Flame,
  Heart,
  Info,
  Leaf,
  Loader2,
  Moon,
  PinOff,
  Play,
  Plus,
  Sparkles,
  Sun,
  Sunrise,
  Sunset,
  Target,
  TrendingUp,
  Trophy,
  Zap,
} from 'lucide-react'
import { Button } from '../components/ui/Button'
import { SkeletonTodayFocus } from '../components/ui/Skeleton'
import { HabitAdjustmentCheckinList } from '../components/habits/HabitAdjustmentCheckinList'
import { HabitPauseRevisitCard } from '../components/habits/HabitPauseRevisitCard'
import { GoalProgressReviewCard } from '../components/automom/GoalProgressReviewCard'
import { ORI_RANK, type OriAskKind } from '../utils/oriAsks'
import { SkillCheckpointCard } from '../components/automom/SkillCheckpointCard'
import {
  SectionCard,
  SectionHeading,
  priorityLabel,
} from './TodayFocusRefined'
import type {
  FocusTaskItem,
  HabitActionItem,
  HabitOccurrenceItem,
  TodayFocusRefinedProps,
  TodayItemLike,
} from './TodayFocusRefined'
import { EnergyModePill } from '../components/rationing/EnergyModeControl'
import {
  permissionToStopCard,
  explainUnits,
  validateEnergyModeCopy,
} from '../utils/energyModeCopy'
import {
  ALL_STATIC_STRINGS,
  WHY_DETAIL,
  parseHHMM,
  COPY,
  computeFreeMinutes,
  dayPhase,
  deriveCoreComplete,
  nextCandidateIndex,
  parseSlotHour,
  selectCandidates,
  isDayClosed,
} from '../utils/todayOneClearThing'
import type { DayPhase, WhyChip } from '../utils/todayOneClearThing'
import { dayShapeWords, executionWalk, queueAfterHero, slotOverlap, taskMeta } from '../utils/dayShape'
import { RowVerb, TodayRow } from '../components/today/TodayRow'
import { dayPillWords, dayTally, habitsKeptWords } from '../utils/dayTally'
import { weekRings } from '../utils/weekStrip'

// Doctrine guard — every static string on this page must pass the
// capacity-not-worth language check. Mirrored by a unit test; this dev assert
// catches a worth-language edit the moment the page renders in development.
if (import.meta.env.DEV) {
  const v = validateEnergyModeCopy(...ALL_STATIC_STRINGS)
  // eslint-disable-next-line no-console
  console.assert(v.valid, 'Worth-language violation in /today copy:', v.violations)
}

// ── tiny storage helpers (private-browsing safe) ────────────────────────────

function safeGet(store: 'session' | 'local', key: string): string | null {
  try {
    return (store === 'session' ? window.sessionStorage : window.localStorage).getItem(key)
  } catch {
    return null
  }
}

function safeSet(store: 'session' | 'local', key: string, value: string): void {
  try {
    ;(store === 'session' ? window.sessionStorage : window.localStorage).setItem(key, value)
  } catch {
    /* private browsing — veil shows each visit, confetti may replay; acceptable */
  }
}

const PHASE_ICON: Record<DayPhase, React.ReactNode> = {
  morning: <Sunrise className="w-4 h-4 text-muted" aria-hidden="true" />,
  midday: <Sun className="w-4 h-4 text-muted" aria-hidden="true" />,
  evening: <Sunset className="w-4 h-4 text-muted" aria-hidden="true" />,
  night: <Moon className="w-4 h-4 text-muted" aria-hidden="true" />,
}

// A habit shown on the hero card (occurrence preferred, definition fallback).
interface HabitCandidate {
  kind: 'occurrence' | 'definition'
  id: string | number
  title: string
  duration?: number
  occ?: HabitOccurrenceItem
}

/**
 * The page's props: the shared refined contract plus two host-supplied fields
 * (added 2026-08-29) that the legacy contract never carried —
 *  - carryoverPrimaryLabel: the backend's tone-guarded CTA label for the
 *    carryover banner (the page used to hardcode "Review").
 *  - planVersion: bumped by the host whenever today's plan changes, so the
 *    prepared-day counters refetch instead of staying frozen at mount.
 */
export type OneClearThingProps = TodayFocusRefinedProps & {
  carryoverPrimaryLabel?: string
  planVersion?: number
  /**
   * Asks raised OUTSIDE this page — the admission inbox, the prepared-day
   * curtain, the release offer, fill-the-gaps — folded into the one zone
   * (owner, 2026-09-08: *"all seem to be different and they are confusing me
   * ... I want 1 guidance"*). They used to render as four more cards stacked
   * above this one, each with its own "Ori ..." heading, so the page offered
   * five entry points and no order between them.
   */
  extraAsks?: Array<{ key: string; kind: OriAskKind; node: React.ReactNode; empty?: boolean }>
  /**
   * Live options the curtain is holding. Passed DOWN rather than read with a
   * hook here: this file's hooks are order-sensitive (see the 2026-08-29 note
   * above `winsPromoted`), and TodayFocus already holds the snapshot.
   */
  preparedLiveCount?: number
  /**
   * The prepared day's top live pick — rule 0 of the Start-here selector.
   *
   * Passed down for the same reason as `preparedLiveCount`: this file's hooks
   * are order-sensitive, and TodayFocus already holds the snapshot.
   */
  preparedTopItemId?: string | null
  /**
   * Today's optional slots and the server's candidates for them. Passed down
   * for the same reason as `preparedLiveCount`: this file's hooks are
   * order-sensitive, and TodayFocus already owns the page's fetches.
   */
  stretch?: import('../services/stretchService').Stretch
  /** Take a stretch TASK on: pins it for today and adds it to the plan. Resolves
   *  true when it happened. Habits have no plan to join, so they only open. */
  onTakeOn?: (taskId: string) => Promise<boolean>
  /** Today's answer to "how did today feel", once given. */
  dayMood?: 'low' | 'okay' | 'good' | 'great' | null
  /**
   * What the recent answers changed, in the server's words — the ONE
   * consequence the question promises. Null unless the days actually say
   * something (see `mood_service.suggest_from_moods`).
   */
  moodSuggestion?: string | null
  onPickMood?: (mood: 'low' | 'okay' | 'good' | 'great') => void
  /** After the energy rung changes: the plan and Stretch both read it, so
   *  the page owner re-fetches both. Falls back to a plain refresh. */
  onCapacityChanged?: () => void | Promise<void>
  /**
   * The Focus card's "Not today" (2026-09-12 mockup): moves the job to
   * tomorrow and frees today — the carryover route, which also takes the
   * task out of today's focus. Resolves once the view has been refetched.
   */
  onNotToday?: (taskId: string) => Promise<void> | void
  /** Quick add from the band: a title is enough. Resolves when saved. */
  onQuickAdd?: (title: string) => Promise<void>
}

export // ── Zones ───────────────────────────────────────────────────────────────
// MODULE SCOPE, deliberately. This lived inside TodayOneClearThing and that
// was a real bug: a component defined during render is a NEW type on every
// render, so React unmounts and remounts the whole subtree and every child
// loses its state. Symptom (owner screenshot 2026-08-30): opening the focus
// panel snapped the deadline carousel back to slide 1 while the panel still
// named the deadline from slide 2.
// One shell for all five, so the page has a single grammar: a name, a
// one-sentence rule, and the contents. A zone whose children all render
// nothing disappears entirely — a fixed shape must not turn an empty day
// into five bureaucratic headings over three quiet sentences.
/**
 * A card's identity, as a colour (2026-09-11, the four-card grid).
 *
 * Static map on purpose: Tailwind's JIT cannot see a class name built at
 * runtime, so `bg-tint-${accent}-wash` would compile to nothing at all and the
 * card would silently render on the bare surface.
 *
 * The tints are `color-mix(… , var(--bg-surface))` (theme.css), so every one of
 * these follows light and dark without a second definition.
 */
/** The four answers, weakest first. Mirrors `mood_service.MOODS` — the store
 *  was empty when this replaced the 0-4 slider, so these names are the
 *  contract on both sides. */
const MOOD_CHOICES = [
  { key: 'low', label: 'Low' },
  { key: 'okay', label: 'Okay' },
  { key: 'good', label: 'Good' },
  { key: 'great', label: 'Great' },
] as const

/** The four faces on the check-in card (the mockup drew faces, not words —
 *  the word sits under each so the face never has to be read alone). */
const MOOD_FACE: Record<(typeof MOOD_CHOICES)[number]['key'], string> = {
  low: '\u{1F614}',
  okay: '\u{1F610}',
  good: '\u{1F642}',
  great: '\u{1F929}',
}

const ACCENT = {
  /** The one thing that matters. The brand colour belongs here, not on the
   *  optional card — see the 2026-09-10 review of the four-card draft. */
  brand: { ground: 'bg-tint-brand-soft', edge: 'border-tint-brand-edge', spine: 'before:bg-brand' },
  /** Finished work. */
  ok: { ground: 'bg-tint-ok-soft', edge: 'border-tint-ok-edge', spine: 'before:bg-ok' },
  /** Wants an answer. Amber, NEVER `bad` — a card whose own rule says
   *  "skip them and nothing breaks" must not be styled as an alarm. */
  warn: { ground: 'bg-tint-warn-soft', edge: 'border-tint-warn-edge', spine: 'before:bg-warn' },
  /** Deliberately the quietest card on the page. */
  none: { ground: 'bg-surface', edge: 'border-soft', spine: 'before:bg-soft' },
} as const

export type ZoneAccent = keyof typeof ACCENT

const Zone = ({ name, rule, tone, accent, order, action, empty, collapsible, defaultOpen = true, children }: {
  name: string
  rule: string
  tone: 'act' | 'quiet'
  /** The card's colour identity. Defaults from `tone` so older calls are unchanged. */
  accent?: ZoneAccent
  /** Grid-order utilities. The DOM order is the PHONE order; this puts the
   *  card back into reading order on a wide screen. */
  order?: string
  action?: React.ReactNode
  empty?: string
  /** Zone owns the collapse, so the sections inside it never repeat the
   *  zone's own name back at the reader one line lower. */
  collapsible?: boolean
  defaultOpen?: boolean
  children: React.ReactNode
}) => {
  const [open, setOpen] = useState(defaultOpen)
  // North Star rule 8 (owner, 2026-09-11): the one-sentence rule under each
  // name was permanent explanation. It is one tap away now, behind ⓘ, and
  // the name has to carry the section on its own.
  const [infoOpen, setInfoOpen] = useState(false)
  const live = React.Children.toArray(children).filter(Boolean)
  if (live.length === 0 && !empty) return null
  const heading = (
    <div className={tone === 'act' ? '[&_h2]:text-app-title [&_h2]:leading-tight min-w-0' : 'min-w-0'}>
      <SectionHeading title={name} />
    </div>
  )
  const skin = ACCENT[accent ?? (tone === 'act' ? 'brand' : 'none')]
  return (
    <section
      aria-label={name}
      className={`relative flex flex-col overflow-hidden rounded-2xl border shadow-sm
        before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:content-['']
        ${skin.spine} ${skin.ground} ${skin.edge} ${order ?? ''}`}
    >
      <div className="flex items-center justify-between gap-2 pl-5 pr-2 py-2">
        {collapsible && live.length > 0 ? (
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            aria-expanded={open}
            aria-label={`${open ? 'Collapse' : 'Expand'} ${name}`}
            className="flex-1 min-w-0 min-h-touch text-left rounded-lg -ml-1 pl-1 hover:bg-surface-alt/40 transition-colors"
          >
            {heading}
          </button>
        ) : (
          <div className="flex-1 min-w-0 py-1">{heading}</div>
        )}
        <span className="flex items-center gap-1 shrink-0">
          {action}
          <button
            type="button"
            onClick={() => setInfoOpen(v => !v)}
            aria-expanded={infoOpen}
            aria-label={COPY.zoneInfo(name)}
            title={COPY.zoneInfo(name)}
            className={`min-h-touch min-w-touch flex items-center justify-center rounded-lg transition-colors ${infoOpen ? 'text-primary bg-surface-alt' : 'text-muted hover:text-secondary hover:bg-surface-alt/60'}`}
          >
            <Info className="w-4 h-4" aria-hidden="true" />
          </button>
          {collapsible && live.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              aria-expanded={open}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${name}`}
              className="min-h-touch min-w-touch flex items-center justify-center rounded-lg text-muted hover:bg-surface-alt/60 transition-colors"
            >
              {open
                ? <ChevronUp className="w-4 h-4" aria-hidden="true" />
                : <ChevronDown className="w-4 h-4" aria-hidden="true" />}
            </button>
          )}
        </span>
      </div>
      {infoOpen && (
        <p className="pl-5 pr-4 pb-3 -mt-1 text-app-micro text-muted">{rule}</p>
      )}
      {live.length === 0 ? (
        <p className="border-t border-soft pl-5 pr-4 py-3 text-app-meta text-muted">{empty}</p>
      ) : (
        (!collapsible || open) && (
          <div className="border-t border-soft pl-5 pr-4 py-4 space-y-3">{children}</div>
        )
      )}
    </section>
  )
}



function TodayOneClearThing(props: OneClearThingProps) {
  const {
    serverDateLabel, onRefresh,
    progress, dailyGoalLabel, isAboveAndBeyond, beyondCount,
    recoveryMode, recoveryMessage, effectiveLimit, dailyFocusLimit,
    focusTasks, dailyHabits, habitOccurrences, inProgressBacklog, timelineEvents,
    completedItems, completedYesterday, hardDeadlineWarnings, backlogSummary,
    carryoverItems, alerts,
  } = props

  // ── Section 0: cross-cutting state ─────────────────────────────────────
  const [refreshTick, setRefreshTick] = useState(0)
  // The band's pill opens the finished list; Quick add opens its row; the
  // Focus card's "Not today" is in flight for one task at a time.
  const [pillOpen, setPillOpen] = useState(false)
  const [quickAddOpen, setQuickAddOpen] = useState(false)
  const [notTodayBusy, setNotTodayBusy] = useState<string | null>(null)
  // Phase computes at mount + on refresh only (no interval — a stale tab keeps
  // its register until refreshed; the deliberate trade for zero mid-session reflow).
  const phase = useMemo<DayPhase>(() => dayPhase(new Date().getHours()), [refreshTick])
  const currentHour = useMemo(() => new Date().getHours(), [refreshTick])
  const nowMinutes = useMemo(() => {
    const d = new Date()
    return d.getHours() * 60 + d.getMinutes()
  }, [refreshTick])
  // The user's LOCAL day, for the day-scoped verbs. The server refuses to
  // guess which day "today" is (SC-35), and the client is the side that knows.
  const localDateISO = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [refreshTick])

  const handleRefresh = () => {
    setRefreshTick(t => t + 1)
    onRefresh()
  }

  // Line-2250 invariant: focusTasks arrives pre-filtered (no completed tasks),
  // so completion is derived from progress.completed — never task statuses.
  const isLowMomentum = recoveryMode === 'low_momentum'
  const incompleteFocus = useMemo(
    () => focusTasks.filter(t => t.status !== 'completed'), [focusTasks])      // defensive
  const effectiveFocus = isLowMomentum ? incompleteFocus.slice(0, effectiveLimit) : incompleteFocus
  const deferredFocus = isLowMomentum ? incompleteFocus.slice(effectiveLimit) : []
  const totalHabits = dailyHabits.length + habitOccurrences.length
  const completedHabits =
    dailyHabits.filter(h => h.status === 'completed').length +
    habitOccurrences.filter(h => h.status === 'completed').length
  // THE day's count. The masthead sentence, the Done ring and its "still to
  // come" line all read this one object (see dayTally.ts for the day three
  // formulas disagreed). Tasks done = the list the Done card names, so the
  // figure can never claim more than the list shows.
  const tally = dayTally({
    tasksDone: completedItems.length,
    tasksOpen: incompleteFocus.length,
    habitsDone: completedHabits,
    habitsTotal: totalHabits,
  })

  const coreComplete = deriveCoreComplete({
    focusCount: focusTasks.length,
    // Task-day EVIDENCE (progress.total is a budget, never 0 — see helper doc):
    completedTaskCount: completedItems.length,
    progressCompleted: progress.completed,
    effectiveLimit,
    remainingEffectiveFocus: effectiveFocus.length,
    totalHabits,
    completedHabits,
  })

  // YESTERDAY's wins say nothing about TODAY's plan — an active user opening a
  // genuinely-empty tomorrow still needs the wide-open invitation (with
  // encouragement-first copy). Only TODAY's content suppresses it.
  const dayHasOtherContent =
    timelineEvents.length > 0 || inProgressBacklog.length > 0 || completedItems.length > 0
  const isWideOpen = totalHabits === 0 && focusTasks.length === 0 && !dayHasOtherContent

  // ── Section 3 input: deadline auto-expansion (safety skips the veil) ────
  const deadlineAutoExpand = hardDeadlineWarnings.some(w =>
    w.level === 'critical' || w.level === 'error' || w.level === 'urgent' ||
    w.days_remaining === 0 || (w.days_overdue ?? 0) > 0)

  // ── Section 4: candidate engine ─────────────────────────────────────────
  const orderedCandidates = useMemo(
    () => selectCandidates(effectiveFocus, currentHour, props.preparedTopItemId),
    [effectiveFocus, currentHour, props.preparedTopItemId])
  // THE walk (North Star rule 5, 2026-09-11): the person's own leads first
  // (started, pinned, due soon — in the selector's order), then the clock,
  // then what has no clock. The hero, the "then" rows, "Not this one →" and
  // focus mode all read this list, so the page can never say "then 15:00"
  // under a 15:45 job again. When the clock promotes a lead over the
  // selector's pick, the reason says so.
  const walk = useMemo(() => {
    const sticky = (c: { why: WhyChip }) =>
      c.why === 'Already started' || c.why === 'Pinned by you' || c.why === 'Due soon'
    const w = executionWalk(orderedCandidates, c => c.task.scheduled_time_slot, sticky)
    if (w.length > 0 && w[0] !== orderedCandidates[0] && !sticky(w[0])
        && parseHHMM(w[0].task.scheduled_time_slot ?? undefined) !== null) {
      return [{ ...w[0], why: 'Next on your schedule' as WhyChip }, ...w.slice(1)]
    }
    return w
  }, [orderedCandidates])
  // Two blocks over the same minutes: said out loud under the job, never
  // silently drawn (rule 5).
  const overlap = useMemo(() => slotOverlap(effectiveFocus, t => t.scheduled_time_slot), [effectiveFocus])
  const [rotation, setRotation] = useState({ index: 0, count: 0 })
  const candidateEntry = walk.length > 0
    ? walk[Math.min(rotation.index, walk.length - 1)]
    : null
  const candidateTask: FocusTaskItem | null = candidateEntry
    ? (effectiveFocus.find(t => t.task_id === candidateEntry.task.task_id) ?? null)
    : null

  // Habit fallback when no task candidate exists.
  const habitCandidate = useMemo<HabitCandidate | null>(() => {
    if (candidateTask) return null
    const occs = habitOccurrences
      .filter(h => h.status !== 'completed')
      .slice()
      .sort((a, b) => {
        const ha = parseSlotHour(a.time_slot); const hb = parseSlotHour(b.time_slot)
        return Math.abs((ha ?? 99) - currentHour) - Math.abs((hb ?? 99) - currentHour)
      })
    if (occs.length > 0) {
      const o = occs[0]
      return { kind: 'occurrence', id: o.id, title: o.title, duration: o.duration_minutes, occ: o }
    }
    const defs = dailyHabits
      .filter(h => h.status !== 'completed')
      .slice()
      .sort((a, b) => (a.start_time ?? '99').localeCompare(b.start_time ?? '99'))
    if (defs.length > 0) {
      const d = defs[0]
      return { kind: 'definition', id: d.id, title: d.title, duration: d.estimated_duration }
    }
    return null
  }, [candidateTask, habitOccurrences, dailyHabits, currentHour])

  // Retired with the veil above. Kept as a constant rather than threaded out
  // of six call sites, so the day-closed branch below still reads plainly.
  const revealed = true
  const [nightOverride, setNightOverride] = useState(false)

  // Props update WITHOUT a remount on in-session refetches (pin/log/carryover
  // paths), so the mount-time initializer above goes stale. One-way sync: a
  // newly-surfaced critical deadline always opens the strip, never closes it
  // (calm never trumps safety). The veil half of this is gone with the veil.
  useEffect(() => {
    if (deadlineAutoExpand) setDeadlinesOpen(true)
  }, [deadlineAutoExpand])

  // Day rollover without a remount (the tab stays open past midnight or the
  // server date changes): yesterday's local UI state must not leak into the
  // new day. revealed re-derives from the NEW day's storage key.
  const prevDateRef = useRef(serverDateLabel)
  useEffect(() => {
    if (prevDateRef.current === serverDateLabel) return
    prevDateRef.current = serverDateLabel
    setRestingMode(false)
    setShowOptional(false)
    setNightOverride(false)
    setAsksExpanded(false)
    setRotation({ index: 0, count: 0 })
    confettiPlayedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverDateLabel])

  // Hero habit starter steps (fetched on reveal; never blocks the title).
  // nightOverride counts as a reveal — 'Show me anyway' must load steps too.
  const [habitSteps, setHabitSteps] = useState<HabitActionItem[] | null>(null)
  const [habitStepsLoading, setHabitStepsLoading] = useState(false)
  useEffect(() => {
    setHabitSteps(null)
    if ((!revealed && !nightOverride) || !habitCandidate || !props.onFetchHabitDetail) return
    let cancelled = false
    setHabitStepsLoading(true)
    props.onFetchHabitDetail(habitCandidate.id)
      .then(d => { if (!cancelled) setHabitSteps(d?.action_items?.slice(0, 3) ?? []) })
      .catch(() => { if (!cancelled) setHabitSteps([]) })
      .finally(() => { if (!cancelled) setHabitStepsLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, nightOverride, habitCandidate?.id, habitCandidate?.kind])

  // ── Section 5: celebration + resting mode ───────────────────────────────
  const [restingMode, setRestingMode] = useState(false)
  const [showOptional, setShowOptional] = useState(false)
  const celebrationCard = useMemo(
    () => permissionToStopCard({ hasOptional: deferredFocus.length + inProgressBacklog.length > 0 }),
    [deferredFocus.length, inProgressBacklog.length])
  const confettiPlayedRef = useRef(false)
  const playConfetti = coreComplete && !confettiPlayedRef.current &&
    safeGet('local', `horton.celebrated.${serverDateLabel}`) !== '1'
  useEffect(() => {
    if (playConfetti) {
      confettiPlayedRef.current = true
      safeSet('local', `horton.celebrated.${serverDateLabel}`, '1')
    }
  }, [playConfetti, serverDateLabel])
  const winsRef = useRef<HTMLDivElement | null>(null)

  // Optional reveal target: smallest deferred item, else first backlog item.
  const optionalItem: { title: string; id: string; kind: 'task' | 'backlog' } | null = useMemo(() => {
    if (deferredFocus.length > 0) {
      const smallest = deferredFocus.slice().sort(
        (a, b) => (a.estimated_duration ?? 9999) - (b.estimated_duration ?? 9999))[0]
      return { title: smallest.title, id: smallest.task_id, kind: 'task' }
    }
    if (inProgressBacklog.length > 0) {
      const first = inProgressBacklog[0]
      return { title: first.title, id: String(first.id), kind: 'backlog' }
    }
    return null
  }, [deferredFocus, inProgressBacklog])

  // ── Section 6: what the rest-of-today timeline may draw from ────────────
  // The hero's own item is excluded so one job never appears twice on one
  // screen; everything else the day holds goes in, and the ORDER is the
  // timeline's single time sort (see components/today/timelineModel.ts).
  //
  // The proximity grouping that used to live here ("Around Now" ±2h, then
  // "Other Habits" / "Other Tasks") was four piles, not a day: it re-decided
  // what mattered on a surface whose whole job is to render, and it hid the
  // clock the work actually sits on (constitution §6 Phase 2 item 1).
  //
  // Only what the hero is ACTUALLY showing may be withheld from the timeline.
  // When the core is done the hero slot holds the celebration card instead of a
  // candidate (renderHeroCard), and on a task-day that finished with habits
  // still open `habitCandidate` is non-null all the same — so withholding it
  // hid a live habit from every surface on the page at once.
  const heroShowsCandidate = !coreComplete
  const spotlightTaskId = heroShowsCandidate ? candidateTask?.task_id : undefined
  // Definition ids and occurrence ids are SEPARATE id spaces (task ids vs
  // occurrence row ids) — exclude the spotlight habit only from its own kind,
  // or an unrelated definition sharing a numeric id would vanish.
  const spotlightDefId = heroShowsCandidate && habitCandidate?.kind === 'definition'
    ? habitCandidate.id : undefined
  const spotlightOccId = heroShowsCandidate && habitCandidate?.kind === 'occurrence'
    ? habitCandidate.id : undefined
  const ledgerHabits = useMemo(() => {
    const defs = dailyHabits.filter(h => h.status !== 'completed' && h.id !== spotlightDefId)
    const occs = habitOccurrences.filter(h => h.status !== 'completed' && h.id !== spotlightOccId)
    return { defs, occs }
  }, [dailyHabits, habitOccurrences, spotlightDefId, spotlightOccId])
  const ledgerTasks = useMemo(
    () => effectiveFocus.filter(t => t.task_id !== spotlightTaskId),
    [effectiveFocus, spotlightTaskId])
  // The two id filters above cover the feeds this page owns. They cannot cover
  // SCHEDULE BLOCKS — those arrive on a third feed keyed by block id, so the
  // hero's own block used to render as a timeline row underneath the hero card
  // (the same job, twice on one screen), and a hero OCCURRENCE left its own
  // DEFINITION row behind. The merge claims the spotlight first instead, by
  // canonical ref and by exact title.
  const spotlightRef = useMemo(() => {
    if (!heroShowsCandidate) return null
    if (candidateTask) {
      return { itemType: 'task' as const, itemId: candidateTask.task_id, title: candidateTask.title }
    }
    if (habitCandidate) {
      // Prefer the HABIT id (shared by the definition and every occurrence of
      // it); habitCandidate.id is an occurrence row id in the occurrence case.
      return {
        itemType: 'habit' as const,
        itemId: habitCandidate.occ?.habit_id ?? habitCandidate.id,
        title: habitCandidate.title,
      }
    }
    return null
  }, [heroShowsCandidate, candidateTask, habitCandidate])
  const completedHabitItems: Array<TodayItemLike | HabitOccurrenceItem> = useMemo(() => [
    ...dailyHabits.filter(h => h.status === 'completed'),
    ...habitOccurrences.filter(h => h.status === 'completed'),
  ], [dailyHabits, habitOccurrences])

  // ── Section 2: how much of today is open ────────────────────────────────
  // The scheduler's own free slots are the answer (owner, 2026-09-12: "use
  // the free-slots endpoint for the free time"): they honour the person's
  // wake, sleep, work hours and work days and see every calendar event. The
  // client formula below stays only as the fallback while that answer is
  // loading or unreachable — it assumes 08:00–21:00 and sees only the blocks
  // this page holds. Refetched with the page (refreshTick) and whenever
  // today's plan moves (planVersion), since both change the gaps.
  const [serverFree, setServerFree] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchFreeSlots(1)
      .then(answer => {
        if (cancelled) return
        const d = new Date()
        setServerFree(todayFreeMinutes(answer?.free_slots, localDateISO, d.getHours() * 60 + d.getMinutes()))
      })
      .catch(() => { if (!cancelled) setServerFree(null) })
    return () => { cancelled = true }
  }, [refreshTick, props.planVersion, localDateISO])
  const localFree = useMemo(
    () => computeFreeMinutes(timelineEvents, nowMinutes), [timelineEvents, nowMinutes])
  const freeMinutes = serverFree ?? localFree
  const [weighedOpen, setWeighedOpen] = useState(false)
  const [whyChipOpen, setWhyChipOpen] = useState<string | null>(null)
  const [focusSetupFor, setFocusSetupFor] = useState<string | null>(null)
  const [focusSaving, setFocusSaving] = useState(false)
  const [deadlineFocus, setDeadlineFocus] = useState<{ id: string; title: string } | null>(null)

  /** The next scheduled thing today, so a focus window can be told to end
   *  before it rather than run into a commitment the user already made. */
  const nextScheduledAt = useMemo(() => {
    const now = new Date()
    const upcoming = timelineEvents
      .map(e => ({ e, mins: parseHHMM(e.start_time || '') }))
      .filter(x => x.mins != null && x.mins > now.getHours() * 60 + now.getMinutes())
      .sort((a, b) => (a.mins as number) - (b.mins as number))[0]
    if (!upcoming) return null
    const m = upcoming.mins as number
    return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
  }, [timelineEvents])

  // ── Stretch: what you could take on beyond today's core ──────────────────
  //
  // Owner, 2026-09-10: *"what are my stretch items that I can target"*, then
  // *"wire the optional_units and the richer stretch pool"*. The server now
  // decides both halves: how many optional slots the ladder rung grants, and
  // ONE next step per active path (plus habits not done) to fill them — which
  // the first version could not do, reading only work already started.
  const stretchItems = useMemo(
    () => (props.stretch?.candidates ?? []).map(c => ({
      key: `${c.type}:${c.id}`,
      id: c.id,
      type: c.type,
      kind: c.path_kind,
      title: c.title,
      note: [c.minutes ? `${c.minutes}m` : null, c.path_title, c.why]
        .filter(Boolean).join(' · ') || null,
    })),
    [props.stretch])
  const [takingOn, setTakingOn] = useState<string | null>(null)

  const [restOpen, setRestOpen] = useState(false)

  // Does the hero actually NAME a job right now? Mirrors the three branches in
  // `renderHeroCard` that return something other than a task: the wide-open
  // invitation, nothing plannable, and the closed night.
  //
  // The queue below reads "then …", which only means anything after a THIS.
  // Seen live on 2026-09-09, the first render of a fresh day: the card said
  // "Today's chapter is closed. Rest is part of the plan." and two rows still
  // hung underneath it saying "then …", following nothing.
  const heroNamesAJob =
    !isWideOpen
    && !!candidateTask
    // The VEIL (line ~1459) and the closed book both hide the job behind a
    // button. Listing "then …" beside either one follows nothing — and past
    // the veil it is worse than untidy: the veil exists so the day arrives one
    // thing at a time, and a queue underneath it hands over the next two.
    && (revealed || nightOverride)

  // What the card would offer next, in ITS order — see `queueAfterHero`. The
  // rotation index is the current pick, so the queue starts one past it.
  const upNextRows = useMemo(
    () => (heroNamesAJob
      ? queueAfterHero(
          walk.map(o => o.task),
          Math.min(rotation.index, Math.max(0, walk.length - 1)) + 1,
        )
      : []),
    [heroNamesAJob, walk, rotation.index])

  const [deadlinesOpen, setDeadlinesOpen] = useState(deadlineAutoExpand)
  // Ask budget for the With You rail — see renderWithYou.
  const [asksExpanded, setAsksExpanded] = useState(false)

  // ── Section 8: With You render condition + pending count ────────────────
  // Defect fix 2026-08-29: this used to include `!props.nudgeFetched ||
  // !!props.hasMotivationNudge`, which was true on every load (the nudge never
  // resolved), so the rail was unconditionally visible with nothing in it.
  const withYouVisible =
    carryoverItems.length > 0 || alerts.length > 0 ||
    (props.habitAdjustmentCheckins || []).length > 0 ||
    !!props.habitAdjustmentCheckin || (props.habitAdjustmentRevisitDue || []).length > 0 ||
    (props.goalReviewPrompts ?? []).length > 0 || (props.skillCheckpoints ?? []).length > 0 ||
    (props.extraAsks ?? []).some(a => !a.empty)
  const pendingCount =
    (carryoverItems.length > 0 ? 1 : 0) +
    ((props.habitAdjustmentCheckins || []).length || (props.habitAdjustmentCheckin ? 1 : 0)) +
    (props.habitAdjustmentRevisitDue || []).length +
    (props.goalReviewPrompts ?? []).length +
    (props.skillCheckpoints ?? []).length +
    (props.extraAsks ?? []).filter(a => !a.empty).length

  // ── Section 9/10 placement ──────────────────────────────────────────────
  const winsPromoted = phase === 'evening' || phase === 'night' || restingMode
  // Yesterday only, since 2026-09-11: the ring beside it names everything
  // finished TODAY, so listing the same titles a second time under "Wins"
  // was the exact repeat the owner flagged across the page.
  const winsVisible = completedYesterday.length > 0
  // Hook-order fix 2026-08-29: these data hooks used to sit ~1,200 lines below,
  // AFTER the props.loading / props.error early returns — so a load or a failure
  // rendered fewer hooks than the ready state and React threw "Rendered more
  // hooks than during the previous render". Every hook must stay ABOVE the two
  // early returns below.
  // Prepared-Day adoption for the glance card: how much of yanamas's plan the
  // user has taken today (ADOPTED or already completed/CONSUMED) over the
  // ACTION entries prepared. Plain honest counts; hidden when no plan.
  const navigate = useNavigate()

  // The rest-of-today timeline runs to the user's OWN sleep anchor. SC-35
  // forbids assuming a day shape, so an unread preference stays null and the
  // timeline simply draws no end marker. One passive preference read; the
  // timeline's own rows ride the page's existing refresh — no polling.
  const [sleepAnchor, setSleepAnchor] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    fetchDayShape()
      .then(shape => { if (!cancelled) setSleepAnchor(shape.sleepTime) })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  // "Your last 5 days" rhythm bars (E9): real completions-per-day series
  // (habits from the execution plane + completed tasks/action-items) from
  // /api/user-analytics/daily-completions. Hidden entirely when the whole
  // window is zero — no decorative bars, ever.
  const [rhythm, setRhythm] = useState<Array<{ date: string; habits: number; items: number }> | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch('/api/user-analytics/daily-completions?days=5', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (cancelled || !b?.success || !Array.isArray(b.series)) return
        setRhythm(b.series)
      })
      .catch(() => undefined)
    return () => { cancelled = true }
  }, [])

  // ── Loading / error (verbatim semantics from the current page) ──────────
  if (props.loading) {
    return (
      <main className="min-h-screen bg-default" aria-busy="true" aria-label="Loading today">
        <SkeletonTodayFocus />
      </main>
    )
  }
  if (props.error) {
    // Defect fix 2026-08-29: this screen exists so a failed fetch never
    // borrows the wide-open empty day's voice. It must stay unmistakably an
    // ERROR (bad-toned border + icon + "couldn't reach" wording), and it must
    // never imply we looked and found nothing.
    return (
      <main
        className="min-h-screen bg-default flex items-center justify-center p-6"
        role="alert"
        aria-label="Today could not be loaded"
      >
        <div className="bg-surface rounded-2xl shadow-sm border-2 border-bad/40 p-8 text-center max-w-sm">
          <AlertCircle className="w-8 h-8 text-bad mx-auto mb-3" aria-hidden="true" />
          <h2 className="text-app-body font-semibold text-primary mb-1">We couldn&rsquo;t reach your day</h2>
          <p className="text-app-meta text-secondary mb-1">{props.error}</p>
          <p className="text-app-micro text-muted mb-4">
            This is a connection problem on our side, not an empty day &mdash; your tasks and habits are still there.
          </p>
          {props.onRetry && (
            <Button onClick={props.onRetry} className="bg-brand text-on-brand min-h-touch">Try Again</Button>
          )}
        </div>
      </main>
    )
  }

  // ── Renderers ───────────────────────────────────────────────────────────

  const renderHeader = () => {
    // The band the mockup drew (2026-09-12): the date, Quick add, and ONE
    // pill for the day — what is left, and how much of it is open — from the
    // one tally (dayTally.ts) and the one free-time formula
    // (computeFreeMinutes). The pill is a disclosure: what you finished
    // today is one tap behind it, so progress reassures without a card of
    // its own (rule 7).
    const { done, total } = tally
    const CIRC = 62.8 // r=10 → 2πr, drawn from 12 o'clock
    const quickAdd = props.onQuickAdd
      ? () => setQuickAddOpen(v => !v)
      : props.onNavigateToCreateTask
    return (
      <header
        data-testid="today-header"
        className="rounded-3xl bg-brand px-4 py-4 text-on-brand shadow-sm sm:px-5"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 [&_svg]:text-on-brand/80">
            {PHASE_ICON[phase]}
            <h1 className="truncate font-display text-lg font-semibold sm:text-xl">{serverDateLabel}</h1>
          </div>
          {quickAdd && (
            <button
              type="button"
              onClick={quickAdd}
              aria-expanded={props.onQuickAdd ? quickAddOpen : undefined}
              data-testid="quick-add-toggle"
              className="inline-flex min-h-touch shrink-0 items-center gap-1 rounded-full px-3 text-app-meta font-semibold text-on-brand transition-colors hover:bg-on-brand/10"
            >
              <Plus className="h-4 w-4" aria-hidden="true" /> {COPY.quickAdd}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setPillOpen(v => !v)}
          aria-expanded={pillOpen}
          aria-label={pillOpen ? COPY.pillCloses : COPY.pillOpens}
          data-testid="day-pill"
          className="mt-3 flex w-full items-center gap-3 rounded-full bg-on-brand/10 px-3 py-2 text-left transition-colors hover:bg-on-brand/15"
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" aria-hidden="true">
            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" className="opacity-25" />
            {total > 0 && (
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                      strokeDasharray={`${(CIRC * done) / total} ${CIRC}`} transform="rotate(-90 12 12)" />
            )}
          </svg>
          <span className="min-w-0 flex-1 text-app-body font-medium leading-snug" data-testid="day-pill-words">
            {dayPillWords(tally, freeMinutes, restingMode)}{habitsKeptWords(tally)}
          </span>
          <ChevronDown className={`h-4 w-4 shrink-0 opacity-80 transition-transform ${pillOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
        </button>
        {isLowMomentum && recoveryMessage && (
          <p className="mt-2 text-app-micro opacity-90">{recoveryMessage}</p>
        )}
      </header>
    )
  }

  /** Quick add, under the band: a title is enough (the list pages' own row). */
  const renderQuickAdd = () => {
    if (!quickAddOpen || !props.onQuickAdd) return null
    return (
      <div data-testid="quick-add-row">
        <TaskQuickAdd
          autoFocus
          placeholder={COPY.quickAddPlaceholder}
          onAdd={async (title) => { await props.onQuickAdd?.(title) }}
          onMore={(title) => navigate('/tasks/create', { state: title ? { title } : undefined })}
        />
      </div>
    )
  }

  /** What you finished today — behind the pill, never a card of its own. */
  const renderDonePanel = () => {
    if (!pillOpen) return null
    return (
      <section aria-label={COPY.zoneSoFar} data-testid="done-panel" className="space-y-3">
        {renderProgressRing() ?? (
          <p className="rounded-2xl border border-soft bg-surface px-4 py-3 text-app-meta text-muted">{COPY.nothingFinishedYet}</p>
        )}
        {renderWins()}
      </section>
    )
  }

  /**
   * FOCUS MODE, as a switch (owner, 2026-09-11), on its own row with the
   * energy rung beside it — the two controls that change how the day is
   * offered. The switch reads as a mode you flip: off here, on inside. This
   * page never renders while focus mode is on, so it is always in its off
   * position here and flipping it opens the mode. ALWAYS present: it first
   * hid itself on a finished day and the owner read that as the feature
   * being removed — so on a day with nothing left it stays, disabled, and
   * says why.
   */
  const renderControls = () => {
    if (!props.onOpenFocusMode && isWideOpen) return null
    return (
      <div data-testid="controls-row" className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-soft bg-surface px-3 py-1">
        {props.onOpenFocusMode ? (
          <button
            type="button"
            role="switch"
            aria-checked={false}
            disabled={props.focusModeAvailable === false}
            title={props.focusModeAvailable === false ? COPY.focusModeEmpty : undefined}
            onClick={props.onOpenFocusMode}
            className={`inline-flex min-h-touch items-center gap-2 rounded-full pr-1 text-app-body font-semibold text-primary transition-colors ${
              props.focusModeAvailable === false ? 'opacity-50 cursor-default' : 'group'
            }`}
          >
            <Flame className="h-4 w-4 shrink-0 text-warn" aria-hidden="true" />
            <span>{COPY.focusModeLabel}</span>
            <span
              aria-hidden="true"
              className="relative ml-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full border border-strong bg-surface-alt transition-colors group-hover:border-brand group-hover:bg-brand-subtle"
            >
              <span className="absolute left-0.5 h-5 w-5 rounded-full border border-soft bg-surface shadow-sm transition-transform group-hover:translate-x-5 group-hover:border-brand group-hover:bg-brand" />
            </span>
          </button>
        ) : <span />}
        {!isWideOpen && (
          <EnergyModePill onChanged={props.onCapacityChanged ?? handleRefresh} />
        )}
      </div>
    )
  }

  /** The week strip: five rings, today last (weekStrip.ts). */
  const renderWeek = () => {
    const rings = weekRings(rhythm ?? [], localDateISO)
    if (rings.length === 0) return null
    const R = 16
    const CIRC = 2 * Math.PI * R
    return (
      <section aria-label={COPY.weekTitle} data-testid="week-strip" title={COPY.weekRule}
               className="rounded-2xl border border-soft bg-surface px-4 py-3">
        <div className="flex items-center justify-around gap-2">
          {rings.map(r => (
            <div key={r.date} className="flex flex-col items-center" title={COPY.weekDay(r.label, r.total)}>
              <svg viewBox="0 0 40 40" className={`h-11 w-11 ${r.isToday ? 'text-ok' : 'text-brand'}`} role="img"
                   aria-label={COPY.weekDay(r.label, r.total)}>
                <circle cx="20" cy="20" r={R} fill="none" stroke="currentColor" strokeWidth="3" className="opacity-20" />
                <circle cx="20" cy="20" r={R} fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                        strokeDasharray={`${CIRC * r.fill} ${CIRC}`} transform="rotate(-90 20 20)" />
                <text x="20" y="24.5" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor"
                      className={r.isToday ? '' : 'opacity-80'}>{r.label}</text>
              </svg>
            </div>
          ))}
        </div>
      </section>
    )
  }

  /**
   * "How did today feel?" — the mockup's Evening check-in card, at the foot
   * of the page. Wired: the answer sets the capacity rung suggested tomorrow
   * (mood_service.suggest_from_moods), and the line under the faces says so.
   */
  const renderCheckin = () => {
    if (!props.onPickMood) return null
    const picked = MOOD_CHOICES.find(m => m.key === props.dayMood) ?? null
    return (
      <section aria-label={COPY.checkinTitle} data-testid="checkin-card"
               className="rounded-2xl border border-tint-warn-edge bg-tint-warn-soft p-4">
        <h2 className="text-app-body font-bold text-primary">{COPY.checkinTitle}</h2>
        <p className="mt-0.5 text-app-meta text-secondary">
          {picked ? COPY.moodPicked(picked.label) : COPY.moodQuestion}
        </p>
        <div className="mt-3 grid grid-cols-4 gap-2" role="radiogroup" aria-label={COPY.moodQuestion}>
          {MOOD_CHOICES.map(m => {
            const on = props.dayMood === m.key
            return (
              <button
                key={m.key}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => props.onPickMood?.(m.key)}
                className={`flex min-h-touch flex-col items-center justify-center gap-0.5 rounded-xl border px-1 py-2 transition-colors ${
                  on ? 'border-brand bg-surface text-primary shadow-sm' : 'border-transparent bg-surface/60 text-secondary hover:bg-surface'
                }`}
              >
                <span className="text-xl leading-none" aria-hidden="true">{MOOD_FACE[m.key]}</span>
                <span className="text-app-micro font-medium">{m.label}</span>
              </button>
            )
          })}
        </div>
        <p className="mt-2 text-app-micro text-muted">{props.moodSuggestion ?? COPY.moodConsequence}</p>
      </section>
    )
  }

  const renderSafetyStrip = () => {
    if (hardDeadlineWarnings.length === 0) return null
    // Owner report 2026-08-30: expanded, the heading and the deadline beneath
    // it had the same weight, the same rounded box and the same colour, so the
    // item did not read as belonging to the header. Now the header is a
    // heading (shared format), and the items sit INDENTED on their own surface
    // behind a left rule — the shape says "these are under that".
    //
    // With more than two, the list becomes a horizontally scrollable row so a
    // long deadline day never pushes the rest of the page off screen.
    return (
      <div>
        <button
          type="button"
          onClick={() => setDeadlinesOpen(v => !v)}
          aria-expanded={deadlinesOpen}
          className="w-full px-4 py-2.5 rounded-xl border border-warn/40 bg-warn/10 flex items-center justify-between gap-2 text-left hover:bg-warn/15 transition-colors"
        >
          <SectionHeading
            title={COPY.deadlinesAttention(hardDeadlineWarnings.length)}
            description={COPY.deadlinesRule}
            icon={<AlertCircle className="w-4 h-4 text-warn" aria-hidden="true" />}
          />
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-warn transition-transform duration-200 ${deadlinesOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
        {deadlinesOpen && (
          <div className="mt-2 ml-3 border-l-2 border-warn/30 pl-3">
            <DeadlineCarousel
              items={hardDeadlineWarnings}
              riskLabel={COPY.risk}
              focusLabel={COPY.focusOnThis}
              openLabel={COPY.workOnThisNow}
              onOpen={props.onDeadlineAction}
              onFocus={(taskId) => {
                // Owner report 2026-08-30: picking a deadline should offer the
                // same check-in setup as picking anything else. It is the same
                // component and the same defaults; only the entry point differs.
                const w = hardDeadlineWarnings.find(x => x.task_id === taskId)
                setDeadlineFocus(w ? { id: taskId, title: w.title } : null)
              }}
            />
            {deadlineFocus && (
              <div className="mt-2">
                <FocusSetup
                  target={deadlineFocus}
                  nextScheduledAt={nextScheduledAt}
                  busy={focusSaving}
                  onCancel={() => setDeadlineFocus(null)}
                  onStart={async (plan) => {
                    setFocusSaving(true)
                    try {
                      if (plan) await saveFocusPlan(deadlineFocus.id, plan)
                    } finally {
                      setFocusSaving(false)
                      const id = String(deadlineFocus.id)
                      setDeadlineFocus(null)
                      ;(props.onStartTask || props.onDeadlineAction)?.(id)
                    }
                  }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // The chip names the rule that matched; one tap gives the reasoning. Owner
  // ask 2026-08-30 — the chip was liked, what it lacked was an explanation of
  // what the rule actually did, at the point of the decision rather than on a
  // separate page.
  // North Star rule 8 (2026-09-11): the chip used to carry the rule's name
  // ("Why this: Next in line") in permanent view. It is a question now; the
  // rule, its meaning and the item's priority are the one tap behind it.
  const renderWhyChip = (why: WhyChip | 'A rhythm for now', extra?: string | null) => {
    const open = whyChipOpen === why
    return (
      <span className="inline-flex flex-col items-start gap-1">
        <button
          type="button"
          onClick={() => setWhyChipOpen(open ? null : why)}
          aria-expanded={open}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-subtle text-brand text-app-micro font-medium hover:bg-brand-subtle/70 transition-colors"
        >
          {COPY.whyThis}
          <ChevronDown className={`w-3 h-3 transition-transform${open ? ' rotate-180' : ''}`} aria-hidden="true" />
        </button>
        {open && (
          <span className="block max-w-prose rounded-lg bg-surface-alt px-2.5 py-1.5 text-app-micro text-secondary leading-relaxed">
            <span className="font-semibold text-primary">{why}. </span>
            {WHY_DETAIL[why]}
            {extra && <span> {extra}</span>}
            <Link to="/today/why" className="ml-1 font-medium text-brand hover:underline">
              {COPY.seeEverything}
            </Link>
          </span>
        )}
      </span>
    )
  }

  const renderStuckHatch = (task: FocusTaskItem) => {
    const stuck = task.status === 'in_progress' && (task.progress ?? 0) < 30 &&
      (props.onEscapeBacklog || props.onEscapeBlocked || props.onOpenAiAssist)
    if (!stuck) return null
    const tiny = walk.find(c =>
      c.task.task_id !== task.task_id &&
      (c.task.reasoning_tags.includes('smallest_first') || (c.task.estimated_duration ?? 9999) <= 15))
    return (
      <div className="mt-3 p-2.5 rounded-lg bg-warn/10 border border-warn">
        <p className="text-app-micro text-warn font-medium mb-1.5">{COPY.feelingStuck}</p>
        <div className="flex flex-wrap gap-2">
          {tiny && (
            <button
              type="button"
              onClick={() => {
                const idx = walk.findIndex(c => c.task.task_id === tiny.task.task_id)
                if (idx >= 0) setRotation(r => ({ index: idx, count: r.count + 1 }))
              }}
              className="min-h-touch px-3 rounded-lg bg-surface border border-soft text-xs font-medium text-primary"
            >
              {COPY.tinyInstead(tiny.task.estimated_duration ?? 5)}
            </button>
          )}
          {props.onOpenAiAssist && (
            <button
              type="button"
              onClick={props.onOpenAiAssist}
              className="min-h-touch px-3 rounded-lg bg-surface border border-soft text-xs font-medium text-primary"
            >
              {isLowMomentum ? COPY.gentleReset : COPY.talkItThrough}
            </button>
          )}
          {props.onEscapeBacklog && (
            <button
              type="button"
              onClick={() => props.onEscapeBacklog!(task.task_id)}
              className="min-h-touch px-3 rounded-lg bg-surface border border-soft text-xs font-medium text-secondary"
            >
              {COPY.toBacklog}
            </button>
          )}
          {props.onEscapeBlocked && (
            <button
              type="button"
              onClick={() => props.onEscapeBlocked!(task.task_id)}
              className="min-h-touch px-3 rounded-lg bg-surface border border-soft text-xs font-medium text-secondary"
            >
              {COPY.blocked}
            </button>
          )}
        </div>
      </div>
    )
  }

  const renderRevealedTask = (task: FocusTaskItem, why: WhyChip) => (
    <div
      key={task.task_id}
      className="ozt-fade cursor-pointer"
      title="Click to open this task"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button,a,input,textarea')) return
        navigate(`/tasks/${task.task_id}`)
      }}
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {renderWhyChip(why, COPY.priorityLine(priorityLabel[task.priority] || task.priority))}
      </div>
      <div className="mt-2 flex items-start gap-3">
        {/* The mockup's checkbox: done is one tap, beside the title, the
            same mark the finished rows carry in their tile. */}
        {props.onCompleteTask && (
          <button
            type="button"
            onClick={() => props.onCompleteTask!(task.task_id)}
            aria-label="Mark done"
            data-testid="hero-done"
            className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 border-strong text-transparent transition-colors hover:border-ok hover:bg-tint-ok-soft hover:text-ok"
          >
            <Check className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
        <button
          type="button"
          onClick={() => props.onOpenTask?.(task.task_id)}
          className="min-w-0 flex-1 text-left"
        >
          <h2 className="text-xl font-semibold text-primary leading-snug">{task.title}</h2>
        </button>
      </div>
      {/* ONE meta line, by the metadata contract (`taskMeta`, North Star
          rule 4): "45 min · 14:15–15:00". The hero's one deliberate extra is
          "Anytime today" when nothing is placed — this is where you decide
          when. Steps read in words, not "1/3". */}
      <div className="mt-2 flex items-center gap-3 flex-wrap text-xs text-secondary tabular-nums">
        <span className="inline-flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
          {taskMeta({
            durationMinutes: task.estimated_duration,
            slot: task.is_scheduled_today ? task.scheduled_time_slot : null,
          }) || (task.future_date_label ? `For ${task.future_date_label}` : COPY.anytimeToday)}
        </span>
        {task.action_items_count > 0 && (
          <span className="inline-flex items-center gap-1"><Zap className="w-3.5 h-3.5" aria-hidden="true" />{COPY.stepsOf(task.action_items_completed, task.action_items_count)}</span>
        )}
        {task.deadline_type === 'hard' && (
          <span
            title={task.deadline_consequence || 'Hard deadline'}
            className="px-1.5 py-0.5 rounded bg-bad/10 text-bad font-bold border border-bad/30 text-app-micro"
          >
            Hard Deadline
          </span>
        )}
      </div>
      {/* Picking something to focus on now offers the check-in setup first
          (owner ask 2026-08-30). It is genuinely optional — "Start now" inside
          the panel needs no decisions — but it is the moment the user actually
          knows when they mean to finish, so it is the honest moment to ask. */}
      {focusSetupFor === task.task_id && (
        <div className="mt-3">
          <FocusSetup
            target={{
              id: task.task_id,
              title: task.title,
              estimatedDuration: task.estimated_duration ?? null,
              scheduledTimeSlot: task.scheduled_time_slot ?? null,
            }}
            nextScheduledAt={nextScheduledAt}
            busy={focusSaving}
            onCancel={() => setFocusSetupFor(null)}
            onStart={async (plan) => {
              setFocusSaving(true)
              try {
                if (plan) await saveFocusPlan(task.task_id, plan)
              } finally {
                setFocusSaving(false)
                setFocusSetupFor(null)
                props.onStartTask?.(task.task_id)
              }
            }}
          />
        </div>
      )}
      {/* The verbs the mockup drew: Start (the page's one filled button),
          Later, Not today. "Later" is the old "Not this one →" — it shows
          the next pick and leaves this one on today. "Not today" moves the
          job to tomorrow and frees today (the carryover route) — never
          "skip" or "cancel": a day is set down, not failed. */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {props.onStartTask && focusSetupFor !== task.task_id && (
          <button
            type="button"
            onClick={() => setFocusSetupFor(task.task_id)}
            data-testid="hero-start"
            className="flex-1 basis-40 min-h-12 px-4 rounded-xl bg-brand text-on-brand font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
          >
            <Play className="w-4 h-4 fill-current" aria-hidden="true" /> {COPY.focusOnThis}
          </button>
        )}
        {walk.length > 1 && (
          <button
            type="button"
            title={COPY.laterTitle}
            data-testid="hero-later"
            onClick={() => setRotation(r => ({
              index: nextCandidateIndex(walk, r.index, r.count),
              count: r.count + 1,
            }))}
            className="min-h-12 px-4 rounded-xl border border-soft bg-surface text-secondary font-medium inline-flex items-center gap-1.5 hover:bg-surface-alt hover:text-primary transition-colors"
          >
            <Clock3 className="w-4 h-4" aria-hidden="true" /> {COPY.laterVerb}
          </button>
        )}
        {props.onNotToday && (
          <button
            type="button"
            title={COPY.notTodayTitle}
            data-testid="hero-not-today"
            disabled={notTodayBusy === task.task_id}
            onClick={async () => {
              setNotTodayBusy(task.task_id)
              try {
                await props.onNotToday?.(task.task_id)
                setRotation({ index: 0, count: 0 })
              } finally {
                setNotTodayBusy(null)
              }
            }}
            className="min-h-12 px-4 rounded-xl border border-soft bg-surface text-secondary font-medium inline-flex items-center gap-1.5 hover:bg-surface-alt hover:text-primary disabled:opacity-60 transition-colors"
          >
            {notTodayBusy === task.task_id && (
              <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
            )}
            {COPY.verbNotToday}
          </button>
        )}
        {(task.reasoning_tags.includes('user_pinned') || task.user_pinned_for_today) && props.onPinTask && (
          <button
            type="button"
            onClick={() => props.onPinTask!(task.task_id, false)}
            aria-label="Unpin from today"
            className="min-h-touch px-3 rounded-xl bg-brand-subtle text-brand flex items-center justify-center"
          >
            <PinOff className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </div>
      {renderStuckHatch(task)}
    </div>
  )

  const renderRevealedHabit = (habit: HabitCandidate) => (
    <div key={String(habit.id)} className="ozt-fade">
      {renderWhyChip('A rhythm for now')}
      <button
        type="button"
        onClick={() => props.onOpenTask?.(String(habit.id))}
        className="mt-2 text-left w-full"
      >
        <h2 className="text-xl font-semibold text-primary leading-snug">{habit.title}</h2>
      </button>
      {taskMeta({ durationMinutes: habit.duration }) && (
        <p className="mt-2 text-xs text-secondary inline-flex items-center gap-1 tabular-nums">
          <Clock className="w-3.5 h-3.5" aria-hidden="true" />{taskMeta({ durationMinutes: habit.duration })}
        </p>
      )}
      {habitStepsLoading && (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted">
          <Loader2 className="w-3.5 h-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" /> {COPY.loadingSteps}
        </p>
      )}
      {habitSteps && habitSteps.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {habitSteps.map(ai => {
            const done = ai.status === 'completed'
            return (
              <button
                key={ai.id}
                type="button"
                onClick={() => props.onToggleHabitActionItem?.(habit.id, ai.id, !done)}
                className={`w-full min-h-touch px-3 rounded-lg border text-left text-xs flex items-center gap-2 transition-colors duration-200 ${
                  done ? 'bg-ok/10 border-ok/30 line-through text-muted' : 'bg-surface border-soft text-primary'
                }`}
              >
                <CheckCircle2 className={`w-4 h-4 shrink-0 ${done ? 'text-ok' : 'text-muted'}`} aria-hidden="true" />
                {ai.title_snapshot || ai.title || ''}
              </button>
            )
          })}
        </div>
      )}
      <div className="mt-4">
        <button
          type="button"
          onClick={() => habit.kind === 'occurrence' && habit.occ
            ? props.onCompleteHabitOccurrence?.(habit.occ)
            : props.onCompleteHabit?.(habit.id)}
          className="w-full min-h-12 px-4 rounded-xl bg-ok text-on-brand font-semibold flex items-center justify-center gap-2 active:scale-95 transition-all"
        >
          <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> {COPY.complete}
        </button>
      </div>
    </div>
  )

  const renderHeroCard = () => {
    // CELEBRATION — permission to stop first, optional strictly second.
    if (coreComplete) {
      return (
        <section className="relative overflow-hidden rounded-2xl bg-ok/10 border border-ok p-5 ozt-fade" aria-label="Core complete">
          {/* Confetti base opacity is 0: the drift keyframes raise it while
              animating, and under prefers-reduced-motion (animation: none) the
              dots stay invisible instead of sticking to the card permanently. */}
          {playConfetti && Array.from({ length: 12 }, (_, i) => (
            <span
              key={i}
              aria-hidden="true"
              className={`absolute w-1.5 h-1.5 rounded-full pointer-events-none ozt-drift ${
                i % 3 === 0 ? 'bg-ok/60' : i % 3 === 1 ? 'bg-warn/60' : 'bg-brand/40'
              }`}
              style={{ left: `${4 + 8 * i}%`, animationDelay: `${i * 50}ms`, opacity: 0 }}
            />
          ))}
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-ok flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-on-brand" aria-hidden="true" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-primary">{celebrationCard.title}</h2>
              <p className="mt-1 text-sm text-secondary leading-relaxed">{celebrationCard.body}</p>
              {isAboveAndBeyond && !!beyondCount && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-secondary" aria-live="polite">
                  <Sparkles className="w-3.5 h-3.5 text-warn" aria-hidden="true" /> {COPY.aboveAndBeyond}
                </p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => {
                setRestingMode(true)
                winsRef.current?.scrollIntoView({ behavior: 'smooth' })
              }}
              className="min-h-12 px-6 rounded-xl bg-ok text-on-brand font-semibold"
            >
              {celebrationCard.primaryLabel}
            </button>
            {celebrationCard.secondaryLabel && optionalItem && (
              <button
                type="button"
                onClick={() => setShowOptional(v => !v)}
                className="min-h-touch px-4 rounded-xl border border-soft text-secondary"
              >
                {COPY.seeOneOptional}
              </button>
            )}
          </div>
          {restingMode && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-secondary">
              <Leaf className="w-3.5 h-3.5 text-ok" aria-hidden="true" /> {COPY.resting}
            </p>
          )}
          {showOptional && optionalItem && (
            <div className="mt-3">
              <TodayRow
                kind="task"
                title={optionalItem.title}
                onOpen={() => props.onOpenTask?.(optionalItem.id)}
                trailing={optionalItem.kind === 'backlog' && props.onPinTask
                  ? <RowVerb label="Pin for today" onClick={() => props.onPinTask!(optionalItem.id, true)} />
                  : props.onLogProgress
                    ? <RowVerb label="Log progress" onClick={() => props.onLogProgress!(optionalItem.id)} />
                    : undefined}
              />
            </div>
          )}
        </section>
      )
    }

    // WIDE-OPEN DAY — the invitation card replaces the hero slot.
    if (isWideOpen) {
      return (
        <section className="bg-surface rounded-2xl shadow-sm border border-soft p-8 text-center" aria-label="Empty day">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-brand-subtle flex items-center justify-center">
            <Sun className="w-7 h-7 text-brand" aria-hidden="true" />
          </div>
          <h3 className="text-base font-semibold text-primary mb-1">{COPY.wideOpenTitle}</h3>
          <p className="text-sm text-secondary max-w-sm mx-auto mb-4">
            {props.emptyStateMessage ||
              (completedYesterday.length > 0 ? COPY.wideOpenBodyAfterWins : COPY.wideOpenBody)}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {props.onNavigateToCreateGoal && (
              <button
                type="button"
                onClick={props.onNavigateToCreateGoal}
                className="flex items-center gap-1.5 min-h-touch px-5 rounded-xl bg-brand text-on-brand text-sm font-medium shadow-sm"
              >
                <Target className="w-4 h-4" aria-hidden="true" /> {COPY.setAGoal}
              </button>
            )}
            {props.onNavigateToCreateTask && (
              <button
                type="button"
                onClick={props.onNavigateToCreateTask}
                className="flex items-center gap-1.5 min-h-touch px-5 rounded-xl border border-soft text-primary text-sm font-medium hover:bg-surface-alt transition-colors"
              >
                <Plus className="w-4 h-4" aria-hidden="true" /> {COPY.createATask}
              </button>
            )}
            {props.onNavigateToSchedule && (
              <button
                type="button"
                onClick={props.onNavigateToSchedule}
                className="flex items-center gap-1.5 min-h-touch px-5 rounded-xl border border-soft text-primary text-sm font-medium hover:bg-surface-alt transition-colors"
              >
                <Calendar className="w-4 h-4" aria-hidden="true" /> {COPY.schedule}
              </button>
            )}
          </div>
        </section>
      )
    }

    // Nothing plannable but the day has other content: no hero card at all.
    if (!candidateTask && !habitCandidate) return null

    // NIGHT — the book is closed (unless safety or explicit override).
    // The LATE side of night only — before dawn the day is ahead, not over.
    if (isDayClosed(currentHour) && !revealed && !nightOverride) {
      return (
        <section className="rounded-2xl bg-surface shadow-sm ring-1 ring-brand-subtle p-5 text-center" aria-label="Day closed">
          <span className="w-12 h-12 mx-auto rounded-full bg-surface-alt flex items-center justify-center">
            <Moon className="w-6 h-6 text-muted" aria-hidden="true" />
          </span>
          <p className="mt-4 text-base text-secondary">{COPY.nightClosed}</p>
          <div className="mt-4 flex items-center justify-center gap-4">
            {props.onNavigateToSchedule && (
              <button
                type="button"
                onClick={props.onNavigateToSchedule}
                className="min-h-touch px-4 text-sm text-brand font-medium"
              >
                {COPY.planTomorrow}
              </button>
            )}
            <button
              type="button"
              onClick={() => setNightOverride(true)}
              className="min-h-touch px-2 text-app-micro text-muted hover:text-secondary"
            >
              {COPY.showMeAnyway}
            </button>
          </div>
        </section>
      )
    }

    // The veil is GONE (owner, 2026-09-11: *"until I click 'Show me' I cannot
    // see any items. The today dashboard is supposed to report it already and
    // does not need a reveal me suspense."*).
    //
    // It existed for privacy — "no task titles visible without an intentional
    // tap", against shoulder-surfing — and that property is genuinely lost
    // here, not merely a click saved. The owner weighed it and chose the
    // dashboard: a page that reports nothing until you ask it to is not a
    // dashboard. Collapsed sections still keep the rest of the day covered.

    // REVEALED
    return (
      <section className="rounded-2xl bg-surface shadow-sm ring-1 ring-brand-subtle p-5" aria-label="One clear thing">
        {candidateTask && candidateEntry
          ? renderRevealedTask(candidateTask, candidateEntry.why)
          : habitCandidate
            ? renderRevealedHabit(habitCandidate)
            : null}
      </section>
    )
  }

  /**
   * The rest of today, in the order it happens (constitution §6 Phase 2 item 1).
   *
   * This page hands the timeline its rows and its clock and nothing else: the
   * ORDER is a time sort inside timelineModel.ts, the CHIPS are the
   * read-model fields today-view stamped on each block, and CORE/OPTIONAL is
   * the day-open snapshot's answer arriving as `plan_role`. No membership or
   * ranking decision is taken here — one screen, one engine per question.
   */
  const renderExecutionTimeline = () => {
    // The six Phase 2 read-model keys ride on each scheduled_events row, and
    // `TimelineEvent extends TimelineSourceEvent`, so this is a checked
    // assignment with NO cast. There used to be an `as unknown as` here; it
    // laundered a host row that had already dropped all six keys, which is
    // exactly why tsc kept exiting 0 while the Flexible/Confirmed distinction
    // rendered nowhere. Do not reintroduce a cast at this seam — the compile
    // error is the regression test.
    const openRow = (row: TimelineRow) => {
      if (row.itemType === 'habit' && row.itemId) navigate(`/habits/${row.itemId}`)
      else if (row.itemId) navigate(`/tasks/${row.itemId}`)
    }
    const occurrenceFor = (row: TimelineRow): HabitOccurrenceItem | undefined => {
      if (row.source === 'habit_occurrence') {
        return habitOccurrences.find(o => String(o.id) === row.refId)
      }
      return row.itemId
        ? habitOccurrences.find(o => String(o.habit_id ?? o.id) === row.itemId)
        : undefined
    }
    return (
      <ExecutionTimeline
        liveOptions={props.preparedLiveCount ?? 0}
        events={timelineEvents}
        tasks={ledgerTasks}
        habitOccurrences={ledgerHabits.occs}
        habitDefinitions={ledgerHabits.defs}
        nowMinutes={nowMinutes}
        sleepAnchor={sleepAnchor}
        localDate={localDateISO}
        spotlight={spotlightRef}
        onOpenRow={openRow}
        onStart={row => { if (row.itemId) props.onStartTask?.(row.itemId) }}
        onDone={row => {
          if (row.itemType === 'habit') {
            const occ = occurrenceFor(row)
            if (occ) props.onCompleteHabitOccurrence?.(occ)
            else if (row.itemId) props.onCompleteHabit?.(row.itemId)
            return
          }
          if (row.itemId) props.onCompleteTask?.(row.itemId)
        }}
        onChanged={handleRefresh}
      />
    )
  }

  const renderAfterThat = (bare = false) => {
    // The timeline draws schedule blocks too, so a day of nothing but calendar
    // blocks still has a rest-of-today. (Before the timeline landed, that day
    // was carried by the read-only Timeline card in the rail, which this zone
    // absorbed — gating on habits/tasks alone would have deleted it.)
    if (totalHabits === 0 && effectiveFocus.length === 0 && timelineEvents.length === 0) return null
    // Owner report 2026-08-30: these were two bare fractions beside two
    // 12px icons ("1/2  0/1") — the least decodable thing on the page, and
    // counting different things from the ring in the masthead. Words now.
    const headerRight = afterThatCounters()
    // `bare`: the Zone shell already renders the name, the rule line and the
    // collapse, so wrapping in a SectionCard here printed "THE REST OF TODAY"
    // twice, one line apart.
    const body = (
      <div className="space-y-4">
          {renderExecutionTimeline()}
          {completedHabitItems.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {completedHabitItems.map(h => (
                <span
                  key={'duration_minutes' in h ? `done-occ-${h.id}` : `done-def-${h.id}`}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ok/10 border border-ok text-xs text-secondary"
                >
                  <CheckCircle2 className="w-3 h-3 text-ok" aria-hidden="true" /> {h.title}
                </span>
              ))}
            </div>
          )}
          {isLowMomentum && deferredFocus.length > 0 && (
            <div className="space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted">
                <Leaf className="w-3.5 h-3.5" aria-hidden="true" /> {COPY.restingForToday(deferredFocus.length)}
              </p>
              <div className="space-y-1.5">
                {deferredFocus.map(t => (
                  <TodayRow
                    key={t.task_id}
                    kind="task"
                    muted
                    title={t.title}
                    meta={taskMeta({ durationMinutes: t.estimated_duration, slot: t.is_scheduled_today ? t.scheduled_time_slot : null })}
                    onOpen={() => props.onOpenTask?.(t.task_id)}
                  />
                ))}
              </div>
              <p className="text-app-micro text-muted italic">{COPY.restingCaption}</p>
            </div>
          )}
          {totalHabits > 0 && focusTasks.length === 0 && (
            <div className="pt-2 border-t border-soft space-y-2">
              <p className="text-xs text-secondary">{COPY.habitsOnTrack}</p>
              <div className="flex flex-wrap gap-2">
                {props.onNavigateToSchedule && (
                  <button type="button" onClick={props.onNavigateToSchedule} className="min-h-touch px-3 rounded-lg border border-soft text-xs font-medium text-primary hover:bg-surface-alt transition-colors">
                    {COPY.scheduleTasks}
                  </button>
                )}
                {props.onNavigateToCreateTask && (
                  <button type="button" onClick={props.onNavigateToCreateTask} className="min-h-touch px-3 rounded-lg border border-soft text-xs font-medium text-primary hover:bg-surface-alt transition-colors">
                    {COPY.newTask}
                  </button>
                )}
                {props.onNavigateToCreateGoal && (
                  <button type="button" onClick={props.onNavigateToCreateGoal} className="min-h-touch px-3 rounded-lg border border-soft text-xs font-medium text-primary hover:bg-surface-alt transition-colors">
                    {COPY.newGoal}
                  </button>
                )}
              </div>
            </div>
          )}
      </div>
    )
    if (bare) return body
    return (
      <SectionCard
        key={`after-${restingMode}`}
        title={COPY.afterThat}
        icon={<Sun className="w-4 h-4 text-warn" />}
        defaultOpen={false}
        right={headerRight}
      >
        {body}
      </SectionCard>
    )
  }

  // The counters, hoisted so the Zone header can carry them when the section
  // renders bare inside a zone.
  const afterThatCounters = () => {
    // The day's shape, without opening the zone (owner, 2026-09-08: *"I also
    // want to peek into how the day is looking like and at what times"*).
    // ONLY the shape: the "N of M tasks done · N of M habits done" fragments
    // that followed it repeated the masthead and the Done card a third time
    // (owner, 2026-09-11). The headline owns the count; this owns WHEN.
    const shape = dayShapeWords(timelineEvents, nowMinutes)
    if (!shape) return null
    return (
      <span className="text-xs text-muted tabular-nums inline-flex items-center gap-1 shrink-0">
        <Clock className="w-3 h-3" aria-hidden="true" />
        {shape}
      </span>
    )
  }

  /**
   * The daily mood ask, retired 2026-09-10 (owner picked the four-section
   * design, which cuts it).
   *
   * Not a taste call: `daily_checkins` holds ZERO rows across the whole
   * database, and nothing outside the write route and the purge list reads the
   * collection. It asked a question every day, stored the answer where no one
   * looked, and its own footnote said it changes nothing — the exact shape of
   * field the owner's own rule says to drop ("show a field only if the user can
   * act on it, else backend-only"). The slider component, its props and its
   * endpoint are untouched; only the daily ask is gone from this page.
   */
  /**
   * The day as one figure, plus what is actually finished.
   *
   * The card used to be a week strip alone in a large box — it said how the
   * WEEK had gone and almost nothing about today. The ring answers "how far
   * am I" at a glance; the list under it names only what is DONE, because the
   * unfinished ones live under Do where they can be acted on (listing them in
   * both places was the one repeat in the 2026-09-10 draft).
   */
  const renderProgressRing = () => {
    // One tally (dayTally.ts). NOT `progress.total` — that is the capacity
    // BUDGET (never 0), so the ring once read "0 of 5" on a day holding one
    // task and one habit. Owner, 2026-09-11: the figure said "4 of 7" over
    // THREE titles — the fourth was the habit, which this list never named.
    // Every finished thing is listed now, tasks then habits, so the number
    // and the names can be checked against each other at a glance. The
    // percentage is gone: it was the same fact a third time, in the one
    // notation the capacity doctrine forbids.
    const { done, total } = tally
    if (total <= 0) return null
    const finished: Array<{ key: string; id: string; title: string; kind: 'task' | 'habit' }> = [
      ...completedItems.map((c, i) => ({
        key: `t-${c.id ?? i}`, id: String(c.id), title: c.title, kind: 'task' as const,
      })),
      ...completedHabitItems.map(h => ({
        key: 'duration_minutes' in h ? `occ-${h.id}` : `def-${h.id}`,
        id: String('duration_minutes' in h ? (h.habit_id ?? h.id) : h.id),
        title: h.title, kind: 'habit' as const,
      })),
    ]
    const SHOWN = 5
    const shown = finished.slice(0, SHOWN)
    const hidden = finished.length - shown.length
    // r=40 → circumference 251.3. Drawn from 12 o'clock, hence the rotation.
    const CIRC = 251.3
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-tint-ok-edge bg-surface p-4">
        <div className="relative h-20 w-20 shrink-0">
          <svg viewBox="0 0 96 96" className="h-20 w-20" aria-hidden="true">
            <circle cx="48" cy="48" r="40" fill="none" stroke="currentColor"
                    strokeWidth="11" className="text-soft" />
            <circle cx="48" cy="48" r="40" fill="none" stroke="currentColor"
                    strokeWidth="11" strokeLinecap="round" className="text-ok"
                    strokeDasharray={`${(CIRC * done) / total} ${CIRC}`}
                    transform="rotate(-90 48 48)" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-app-body font-bold tabular-nums leading-none">{COPY.doneOf(done, total)}</span>
          </div>
        </div>
        <div className="min-w-0">
          {shown.length > 0 ? (
            <ul className="space-y-1.5">
              {shown.map(f => (
                <li key={f.key}>
                  <TodayRow
                    kind={f.kind}
                    done
                    title={f.title}
                    onOpen={() => (f.kind === 'habit' ? navigate(`/habits/${f.id}`) : props.onOpenTask?.(f.id))}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-app-meta text-muted">{COPY.nothingFinishedYet}</p>
          )}
          {hidden > 0 && (
            <p className="mt-1 text-app-micro text-muted">{COPY.moreDone(hidden)}</p>
          )}
          {/* No "N still to come" here: the masthead already says "N things
              left today", and one fact gets one expression (rule 2). */}
        </div>
      </div>
    )
  }

  // "How did today feel?" moved to the masthead beside the energy pill
  // (owner, 2026-09-11), and on 2026-09-12 to the check-in card at the foot
  // of the page — see renderCheckin.


  // Defect fix 2026-08-29: this rail is one of the two chokepoints where
  // Auto-Mom's asks reach the user, and it had NO budget — seven surfaces each
  // rendered N instances, so a struggling week could stack a dozen questions in
  // one panel. Doctrine splits the asks in two and only one half is capped:
  //
  //   USER_PULL + SAFETY — the carryover decision the user is owed, and alerts
  //     (hard deadlines, critical reminders). Governed by FIDELITY: the user
  //     asked to be held to these. UNBUDGETED, and rendered FIRST. Un-ignorable
  //     once is enough, so each still appears once — but never budgeted away.
  //
  //   AUTO-MOM PROPOSALS — pause revisits, habit check-ins, goal reviews,
  //     skill checkpoints. Governed by RESTRAINT: the system asks for little.
  //     Capped at ASK_BUDGET in TOTAL (not per type); the remainder stays one
  //     pull away behind "N more when you're ready" — nothing is removed, only
  //     un-asked, the same affordance HabitAdjustmentCheckinList uses.
  const ASK_BUDGET = 2

  const renderWithYou = (bare = false) => {
    if (!withYouVisible) return null

    // Auto-Mom's proposals, in the order they earn the budget: a habit the
    // user themself paused (closest to their own commitment), then the habit
    // check-in list (which self-caps to ONE and carries its own pull), then
    // goal reviews, then skill checkpoints.
    const checkinList = (props.habitAdjustmentCheckins || []).length > 0
      ? (props.habitAdjustmentCheckins || [])
      : (props.habitAdjustmentCheckin ? [props.habitAdjustmentCheckin] : [])
    const proposals: Array<{ key: string; kind: OriAskKind; node: React.ReactNode }> = []
    for (const item of (props.habitAdjustmentRevisitDue || []) as any[]) {
      proposals.push({
        key: `revisit-${item.habit_def_id}`,
        kind: 'decide',
        node: (
          <HabitPauseRevisitCard
            item={item}
            onRestart={(habitDefId) => {
              const promise = props.onHabitAdjustmentRestart?.(habitDefId)
              if (promise) {
                promise.then(() => props.onHabitAdjustmentRefresh?.())
              }
            }}
            onKeepPaused={props.onHabitAdjustmentPause ? async (habitDefId, payload) => {
              await props.onHabitAdjustmentPause?.(habitDefId, {
                paused_until: payload.paused_until,
                source: payload.source,
              })
              props.onHabitAdjustmentRefresh?.()
            } : undefined}
            onArchive={props.onHabitAdjustmentArchive ? async (habitDefId, payload) => {
              await props.onHabitAdjustmentArchive?.(habitDefId, payload)
              props.onHabitAdjustmentRefresh?.()
            } : undefined}
          />
        ),
      })
    }
    if (checkinList.length > 0 &&
      props.onHabitAdjustmentShown && props.onHabitAdjustmentRespond &&
      props.onHabitAdjustmentDiagnose && props.onHabitAdjustmentChooseAction &&
      props.onHabitAdjustmentConfirm && props.onHabitAdjustmentPause) {
      proposals.push({
        key: 'habit-checkins',
        kind: 'decide',
        node: (
          <HabitAdjustmentCheckinList
            checkins={checkinList}
            markShown={props.onHabitAdjustmentShown}
            respond={props.onHabitAdjustmentRespond}
            diagnose={props.onHabitAdjustmentDiagnose}
            chooseAction={props.onHabitAdjustmentChooseAction}
            confirm={props.onHabitAdjustmentConfirm}
            pause={props.onHabitAdjustmentPause}
            onDone={() => props.onHabitAdjustmentRefresh?.()}
          />
        ),
      })
    }
    if (props.onGoalReviewShown && props.onGoalReviewRespond) {
      for (const prompt of (props.goalReviewPrompts ?? [])) {
        proposals.push({
          key: `goal-review-${prompt.id}`,
          kind: 'celebrate',
          node: (
            <GoalProgressReviewCard
              prompt={prompt}
              markShown={props.onGoalReviewShown!}
              respond={props.onGoalReviewRespond!}
              onDone={result => props.onGoalReviewDone?.(result)}
            />
          ),
        })
      }
    }
    if (props.onSkillCheckpointShown && props.onSkillCheckpointRespond) {
      for (const prompt of (props.skillCheckpoints ?? [])) {
        proposals.push({
          key: `skill-checkpoint-${prompt.id}`,
          kind: 'decide',
          node: (
            <SkillCheckpointCard
              prompt={prompt}
              markShown={props.onSkillCheckpointShown!}
              respond={props.onSkillCheckpointRespond!}
              onDone={result => props.onSkillCheckpointDone?.(result)}
            />
          ),
        })
      }
    }
    // One list, one order. Work you could do now, then a decision that BLOCKS
    // scheduling, then structure, then the rest — see `ORI_RANK`. Ties keep the
    // order they were pushed in, which is each source's own priority.
    const ranked = [...proposals, ...(props.extraAsks ?? [])]
      .map((p, i) => ({ ...p, i }))
      .sort((a, b) => ORI_RANK[a.kind] - ORI_RANK[b.kind] || a.i - b.i)
    // An ask that has reported itself empty renders nothing, so it must not
    // take a budget slot or be counted — but every ask stays MOUNTED, IN ONE
    // LIST, IN A STABLE POSITION. Visibility is a class, never a choice
    // between two arrays.
    //
    // Moving a node between arrays is an unmount plus a remount: the card
    // refetched, reported 0 while loading, was moved back, and looped — 172
    // requests to /api/admission/v1/proposals in one page view (2026-09-08).
    // It is the same hazard as the render-defined component noted above.
    let liveSeen = 0
    const shown = ranked.map(p => {
      const empty = !!(p as { empty?: boolean }).empty
      const held = !empty && !asksExpanded && liveSeen >= ASK_BUDGET
      if (!empty) liveSeen += 1
      return { ...p, visible: !empty && !held }
    })
    const liveCount = liveSeen
    const heldBack = liveCount - shown.filter(p => p.visible).length

    // Same rule as `The rest of today`: inside a Zone the shell already names
    // this and owns the collapse, so the SectionCard would print "yanamas
    // asks" twice, one line apart.
    const body = (
      <div className="space-y-3">
            {/* ── USER_PULL + SAFETY: unbudgeted, and first ──────────────── */}
            {carryoverItems.length > 0 && (
              <div className="p-3 rounded-xl bg-brand-subtle border border-brand-subtle">
                <p className="text-app-body font-semibold text-primary">{props.carryoverTitle || 'Carryover needs a decision'}</p>
                <p className="mt-0.5 text-app-meta text-secondary">{props.carryoverBody || `${carryoverItems.length} tasks from before.`}</p>
                {props.onReviewCarryover && (
                  <Button onClick={props.onReviewCarryover} variant="outline" className="mt-2 h-7 text-app-meta px-3" size="sm">{props.carryoverPrimaryLabel || 'Review'}</Button>
                )}
              </div>
            )}
            {alerts.slice(0, 3).map(alert => (
              <div key={alert.id} className="p-3 rounded-xl border border-soft bg-surface">
                <div className="flex items-center gap-1.5">
                  <p className="text-app-body font-semibold text-primary">{alert.title}</p>
                  {alert.severity === 'critical' && <span className="w-1.5 h-1.5 rounded-full bg-bad" aria-hidden="true" />}
                  {alert.severity === 'warning' && <span className="w-1.5 h-1.5 rounded-full bg-warn" aria-hidden="true" />}
                </div>
                <p className="mt-0.5 text-app-meta text-secondary leading-relaxed">{alert.message}</p>
                {alert.action?.label && (
                  <Button
                    onClick={() => props.onAlertAction?.(alert.id, alert.action?.url)}
                    variant="outline"
                    className="mt-2 h-7 px-2 text-app-micro"
                    size="sm"
                  >
                    {alert.action.label}
                  </Button>
                )}
              </div>
            ))}
            {/* ── AUTO-MOM PROPOSALS: capped at ASK_BUDGET, rest one pull away ── */}
            {shown.map(p => (
              <div key={p.key} className={p.visible ? undefined : 'hidden'}>{p.node}</div>
            ))}
            {heldBack > 0 && (
              <button
                type="button"
                onClick={() => setAsksExpanded(true)}
                className="w-full min-h-touch rounded-xl border border-soft px-3 py-3 text-left text-xs font-medium text-brand hover:bg-surface-alt"
              >
                {heldBack} more when you are ready
              </button>
            )}
            {asksExpanded && proposals.length > ASK_BUDGET && (
              <button
                type="button"
                onClick={() => setAsksExpanded(false)}
                className="w-full min-h-touch rounded-xl border border-soft px-3 py-3 text-left text-xs font-medium text-secondary hover:bg-surface-alt"
              >
                Ask me about fewer things
              </button>
            )}
          </div>
    )
    if (bare) return body
    return (
      <div className={restingMode ? 'transition-opacity duration-500' : ''}>
        <SectionCard
          key={`withyou-${pendingCount > 0}`}
          title={COPY.withYou}
          icon={<Heart className="w-4 h-4 text-brand" />}
          defaultOpen={pendingCount > 0}
          right={<span className="text-app-micro text-muted font-medium">{withYouCounter()}</span>}
        >
          {body}
        </SectionCard>
      </div>
    )
  }

  /** The zone header carries this when `renderWithYou` renders bare. */
  const withYouCounter = () => {
    if (!withYouVisible) return null
    const pending = pendingCount
    return pending > 0 ? COPY.withYouCount(pending) : COPY.updatesCount(alerts.length)
  }

  const renderWins = () => {
    if (!winsVisible) return null
    return (
      <div ref={winsRef}>
        <SectionCard
          key={`wins-${winsPromoted}`}
          title={COPY.wins}
          icon={<Trophy className="w-4 h-4 text-warn" />}
          defaultOpen={winsPromoted}
          right={
            <span className="text-app-micro text-muted font-medium">
              {COPY.yesterdayGlow(completedYesterday.length)}
            </span>
          }
        >
          <div className="space-y-3">
            {completedYesterday.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-secondary">{COPY.yesterday}</p>
                <div className="flex flex-wrap gap-1.5">
                  {completedYesterday.slice(0, 6).map(item => (
                    <span
                      key={item.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-warn/10 border border-warn text-xs text-secondary"
                    >
                      <CheckCircle2 className="w-3 h-3 text-ok" aria-hidden="true" /> {item.title}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <button
                type="button"
                onClick={() => setWeighedOpen(v => !v)}
                aria-expanded={weighedOpen}
                className="flex items-center gap-1 text-app-micro text-muted min-h-touch"
              >
                {COPY.howWeighed}
                <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${weighedOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {weighedOpen && (
                <div className="space-y-1">
                  <p className="text-xs text-secondary leading-relaxed">
                    {COPY.weighedSentence(progress.total, dailyGoalLabel, progress.completed, progress.inProgress)}{' '}
                    {explainUnits({ releasedUnits: effectiveLimit, committedCeiling: dailyFocusLimit })}
                  </p>
                  {backlogSummary.skipped > 0 && (
                    <p className="text-app-micro text-muted">{COPY.setDown(backlogSummary.skipped)}</p>
                  )}
                  {/* The only way into the full inventory. Deliberately here,
                      behind a disclosure the user already opened to ask "why?"
                      — /today must stay a push surface bounded to one thing,
                      and the pull surface has to be reached on purpose. */}
                  <Link
                    to="/today/why"
                    className="inline-block text-app-micro font-medium text-brand hover:underline"
                  >
                    {COPY.seeEverything}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </SectionCard>
      </div>
    )
  }

  const dimWhenResting = restingMode ? 'opacity-60 transition-opacity duration-500' : ''

  // Was one right-hand rail (owner ask 2026-08-24) holding six unrelated
  // cards. Split 2026-08-30 so each piece can sit in the zone it belongs to;
  // since 2026-09-12 only the schedule half is left here — the week became
  // the ring strip (renderWeek) and wins live behind the band's pill.
  const renderRail = () => {
    // A wrapper <div> counts as a child even when everything inside it is
    // null, which is how an empty day still drew a zone heading and a rule
    // sentence over nothing (2026-09-06 audit). This rail has exactly one
    // possible occupant, so it can say plainly that it has none.
    if (inProgressBacklog.length === 0) return null
    return (
      <div className="space-y-3">
        {/* The read-only Timeline card that used to sit here was PROMOTED into
            the rest-of-today execution timeline above (constitution §6 Phase 2
            item 1). Keeping both would have put two answers to "what is left
            today?" on one screen, one of them un-actionable. */}
        {inProgressBacklog.length > 0 && (
          <SectionCard
            title={COPY.inProgressTitle}
            icon={<Play className="w-4 h-4 text-brand" />}
            count={inProgressBacklog.length}
            defaultOpen={false}
          >
            <div className="space-y-2">
              <p className="text-app-micro text-muted">{COPY.inProgressCaption}</p>
              {inProgressBacklog.map(task => (
                <div key={task.id} className="p-3 rounded-xl border border-soft bg-surface hover:border-brand transition-all">
                  <button
                    type="button"
                    onClick={() => props.onOpenTask?.(String(task.id))}
                    className="w-full text-left mb-2"
                  >
                    <h4 className="text-sm font-medium text-primary truncate">{task.title}</h4>
                  </button>
                  <div className="flex gap-2">
                    {props.onPinTask && (
                      <button
                        type="button"
                        onClick={() => props.onPinTask!(String(task.id), true)}
                        disabled={props.pinningTaskId === String(task.id)}
                        aria-label={`Pin ${task.title} to today`}
                        className="flex items-center gap-1.5 min-h-touch px-3 py-2 text-xs font-medium rounded-lg border border-brand-subtle text-brand hover:bg-brand-subtle transition-colors"
                      >
                        {props.pinningTaskId === String(task.id)
                          ? <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                          : 'Pin'}
                      </button>
                    )}
                    {props.onLogProgress && (
                      <button
                        type="button"
                        onClick={() => props.onLogProgress!(String(task.id))}
                        aria-label={`Log progress for ${task.title}`}
                        className="flex items-center gap-1.5 min-h-touch px-3 py-2 text-xs font-medium rounded-lg border border-ok text-ok hover:bg-ok/10 transition-colors"
                      >
                        <TrendingUp className="w-4 h-4" aria-hidden="true" /> Log
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {(backlogSummary.pending > 0 || backlogSummary.in_progress > 0) && (
                <div className="flex items-center gap-2 text-app-micro text-muted pt-2 border-t border-soft">
                  <span className="font-medium">{COPY.backlogLabel}</span>
                  {backlogSummary.pending > 0 && <span>{COPY.pendingCount(backlogSummary.pending)}</span>}
                  {backlogSummary.skipped > 0 && <span>{COPY.backlogSetDown(backlogSummary.skipped)}</span>}
                </div>
              )}
            </div>
          </SectionCard>
        )}
      </div>
    )
  }

  // Computed here rather than inline so the zone can tell whether it has
  // anything at all: a wrapper element is always a truthy child.
  const restOfToday = renderAfterThat(true)
  const scheduleRail = renderRail()

  // ── Page ────────────────────────────────────────────────────────────────
  //
  // The phone layout the owner drew on 2026-09-12, top to bottom: the band
  // (date · Quick add · the day's pill), what is due on a clock, the
  // Focus-mode switch beside the energy rung, then the cards — Reminders
  // held to the day · Focus · Up next with Stretch beneath it · Needs you ·
  // the week · the evening check-in. One column on a phone; on a wide screen
  // the same DOM order wraps into two columns (the first three cards left,
  // the last three right) — nothing reorders per breakpoint.
  //
  // The hierarchy contract (North Star rule 6) still holds: Focus is the
  // only brand-coloured card and carries the page's only filled button;
  // Stretch is the quietest thing on the page, under Up next; Needs you is
  // amber and starts collapsed with its count. Progress reassures from the
  // pill (rule 7) and opens to the finished list on a tap, so it no longer
  // needs a card that restates the count.
  const upNextRule = props.stretch
    ? `${COPY.zoneUpNextRule} ${COPY.stretchRule(props.stretch.mode, props.stretch.core_units, props.stretch.optional_units)}`
    : `${COPY.zoneUpNextRule} ${COPY.stretchRuleShort}`
  const stretchEmptyLine = props.stretch && props.stretch.optional_units <= 0
    ? COPY.stretchNoSlots(props.stretch.mode)
    : COPY.stretchEmpty

  return (
    <main className="min-h-screen bg-default" aria-label="Today">
      <style>{`
        @keyframes ozt-pop { 0% { transform: scale(.3); opacity: 0 } 70% { transform: scale(1.08) } 100% { transform: scale(1); opacity: 1 } }
        .ozt-pop { animation: ozt-pop 360ms ease-out both }
        @keyframes ozt-drift { from { transform: translateY(-90px) rotate(0); opacity: 0 } 20% { opacity: 1 } to { transform: translateY(0) rotate(40deg); opacity: 0 } }
        .ozt-drift { animation: ozt-drift 900ms ease-out both }
        @keyframes ozt-fade { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }
        .ozt-fade { animation: ozt-fade 500ms ease-out both }
        @media (prefers-reduced-motion: reduce) { .ozt-pop, .ozt-drift, .ozt-fade { animation: none !important } }
      `}</style>
      <div className="mx-auto max-w-2xl space-y-3 px-3 py-3 sm:px-6 sm:py-4 lg:max-w-5xl" data-testid="today-page">
        {renderHeader()}
        {renderQuickAdd()}
        {renderDonePanel()}

        {/* What is held to a CLOCK today — the mockup's bar. Renders nothing
            when nothing is due; the Reminders card below owns the rest, and
            a hard deadline still gets its own strip (calm never trumps
            safety). */}
        <ObligationStrip variant="due" />
        {renderSafetyStrip()}
        {renderControls()}

        <div className="grid gap-3 lg:grid-cols-2 lg:items-start">
          <div className="min-w-0 space-y-3">

            {/* REMINDERS held to the day, no clock (the mockup's "All-Day").
                Quiet on purpose: they are yours, not asks. */}
            <Zone name={COPY.zoneReminders} rule={COPY.zoneRemindersRule} tone="quiet"
                  accent="none" empty={COPY.zoneRemindersEmpty}>
              <ObligationStrip variant="allday" />
            </Zone>

            {/* FOCUS — the one thing. The only brand card; the only filled
                button. The rest of today stays one disclosure away. */}
            <Zone name={COPY.zoneFocus} rule={COPY.zoneFocusRule} tone="act" accent="brand">
              {renderHeroCard()}
              {overlap && (
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg bg-tint-warn-soft px-3 py-2 text-app-micro text-secondary">
                  <AlertCircle className="w-3.5 h-3.5 text-warn shrink-0" aria-hidden="true" />
                  <span>{COPY.overlapLine(overlap.a.title, overlap.b.title, overlap.at)}</span>
                  <button type="button" onClick={() => setRestOpen(true)}
                          className="font-medium text-brand hover:underline min-h-touch">
                    {COPY.fixInTimeline}
                  </button>
                </div>
              )}
              <button
                type="button"
                onClick={() => setRestOpen(v => !v)}
                aria-expanded={restOpen}
                className="mt-3 min-h-touch inline-flex items-center gap-1.5 text-app-micro text-muted hover:text-secondary"
              >
                {afterThatCounters() ?? COPY.afterThat}
                <ChevronDown className={`w-3 h-3 transition-transform${restOpen ? ' rotate-180' : ''}`} aria-hidden="true" />
              </button>
              {restOpen && (
                <div className="mt-2">
                  {restOfToday && <div className={dimWhenResting}>{restOfToday}</div>}
                  {/* SC-13, the third leg SC-14 gates HOLD-first on: if the
                      assistant moved anything in today's plan, the record is one
                      tap away here. It renders NOTHING on a day nothing moved —
                      no empty state, no badge, nothing that fires on its own. */}
                  <PlanAdjustments localDate={localDateISO} refreshKey={refreshTick} />
                  {scheduleRail}
                </div>
              )}
            </Zone>

            {/* UP NEXT — what follows the one thing, then Stretch beneath it
                (the mockup's "Up Next / Stretch Goals (Optional)"). Each
                queued row OPENS its item (owner, 2026-09-11). Stretch stays
                the quietest thing on the page: a subheading, never a card
                of its own, and "Take on" DOES the thing. */}
            <Zone name={COPY.zoneUpNext} rule={upNextRule} tone="quiet" accent="none">
              {upNextRows.length > 0 ? (
                <ol className="space-y-1.5" data-testid="up-next">
                  {upNextRows.map(r => (
                    <li key={r.key}>
                      <TodayRow
                        kind="task"
                        prefix={COPY.thenAt}
                        title={r.title}
                        meta={r.meta}
                        onOpen={r.id && props.onOpenTask ? () => props.onOpenTask?.(r.id as string) : undefined}
                      />
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-app-meta text-muted" data-testid="up-next-empty">
                  {heroNamesAJob ? COPY.upNextEmpty : COPY.restOfDayClear}
                </p>
              )}
              <div className="border-t border-soft pt-3">
                <p className="text-app-micro font-semibold uppercase tracking-wide text-muted">{COPY.stretchSub}</p>
                {stretchItems.length > 0 ? (
                  <div className="mt-2 space-y-1.5" data-testid="stretch-rows">
                    {stretchItems.map(item => {
                      const busy = takingOn === item.id
                      const open = () => navigate(item.type === 'habit' ? `/habits/${item.id}` : `/tasks/${item.id}`)
                      return (
                        <TodayRow
                          key={item.key}
                          kind={item.kind === 'skill' ? 'skill' : item.kind === 'habit' ? 'habit' : item.type === 'habit' ? 'habit' : 'task'}
                          title={item.title}
                          meta={item.note}
                          onOpen={open}
                          trailing={item.type === 'task' && props.onTakeOn
                            ? <RowVerb label={COPY.stretchTakeOn} busy={busy} onClick={async () => {
                                setTakingOn(item.id)
                                try { await props.onTakeOn?.(item.id) } finally { setTakingOn(null) }
                              }} />
                            : undefined}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <p className="mt-1 text-app-meta text-muted" data-testid="stretch-empty">{stretchEmptyLine}</p>
                )}
              </div>
            </Zone>
          </div>

          <div className="min-w-0 space-y-3">

            {/* NEEDS YOU — every question the app wants to ask, from every
                subsystem, in ONE place. Amber, never red: its own rule says
                skipping it breaks nothing. Collapsed by default with its
                count; empty, it says so in one line. */}
            <Zone
              accent="warn"
              name={COPY.zoneAsks}
              rule={COPY.zoneAsksRule}
              tone="quiet"
              collapsible
              defaultOpen={false}
              empty={COPY.zoneAsksEmpty}
              action={
                withYouCounter()
                  ? <span className="text-app-micro text-muted font-medium">{withYouCounter()}</span>
                  : null
              }
            >
              {props.asks}
              {renderWithYou(true)}
            </Zone>

            {renderWeek()}
            {renderCheckin()}
          </div>
        </div>
      </div>
    </main>
  )
}

export default TodayOneClearThing
